import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/I18nContext";
import * as classesService from "../services/classes";
import * as lessonsService from "../services/lessons";
import * as programsService from "../services/programs";
import * as studentsService from "../services/students";
import * as homeworksService from "../services/homeworks";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import Admin from "./Admin";
import PackDetails from "./PackDetails";
import StudentShell from "../components/StudentShell/StudentShell";
import { dedupeTeacherSessions } from "../utils/teacherSessionDedupe";
import { getAvatarUrl as resolveAvatarUrl } from "../utils/avatar";
import "./Dashboard.css";

const STATUS_KEYS = {
  scheduled: "lessons.statusScheduled",
  completed: "lessons.statusCompleted",
  cancelled: "lessons.statusCancelled",
};

function makeDateTimeFormatter(language) {
  const locale = language === "ar" ? "ar-TN" : language || "en";
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function formatDateTime(value, formatter) {
  if (!value) {
    return "-";
  }
  try {
    return formatter.format(new Date(value));
  } catch (error) {
    return value;
  }
}

const now = () => new Date().getTime();

const MEETING_JOIN_WINDOW_MINUTES = 120;

function parseDateValue(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

function startOfWeekSunday(value) {
  const date = startOfDay(value);
  date.setDate(date.getDate() - date.getDay());
  return date;
}

function withOccurrenceScheduledAt(lesson, occurrenceDate) {
  if (!lesson || !occurrenceDate) return lesson;
  const iso = occurrenceDate.toISOString();
  return {
    ...lesson,
    scheduled_at: iso,
    __base_scheduled_at: lesson.scheduled_at,
    __occurrence_scheduled_at: iso,
  };
}

function getWeeklyOccurrenceInWeekStarting(lesson, weekStart) {
  const anchor = parseDateValue(lesson?.scheduled_at);
  if (!anchor) return null;

  const targetDow = anchor.getDay();
  const targetHours = anchor.getHours();
  const targetMinutes = anchor.getMinutes();
  const targetSeconds = anchor.getSeconds();
  const targetMs = anchor.getMilliseconds();

  const start = startOfDay(weekStart);
  const candidate = new Date(start);

  const delta = (targetDow - candidate.getDay() + 7) % 7;
  candidate.setDate(candidate.getDate() + delta);
  candidate.setHours(targetHours, targetMinutes, targetSeconds, targetMs);

  if (candidate.getTime() < anchor.getTime()) return null;
  return candidate;
}

function getRelevantWeeklyOccurrenceStart(lesson, referenceDate = new Date()) {
  const anchor = parseDateValue(lesson?.scheduled_at);
  if (!anchor) return null;

  const ref = parseDateValue(referenceDate) || new Date();
  if (ref.getTime() < anchor.getTime()) return anchor;
  const weekStart = startOfWeekSunday(ref);
  const occurrence =
    getWeeklyOccurrenceInWeekStarting(lesson, weekStart) || anchor;
  if (!occurrence) return null;

  const durationMinutes = Number(lesson?.duration_minutes) || 60;
  const endTs = occurrence.getTime() + durationMinutes * 60 * 1000;
  if (ref.getTime() <= endTs) {
    return occurrence;
  }

  const next = new Date(occurrence);
  next.setDate(next.getDate() + 7);
  return next;
}

function isWithinMeetingJoinWindow(
  lesson,
  minutesBefore = MEETING_JOIN_WINDOW_MINUTES,
) {
  if (!lesson?.scheduled_at) return false;
  const scheduledTs = new Date(lesson.scheduled_at).getTime();
  if (!Number.isFinite(scheduledTs)) return false;
  return now() >= scheduledTs - minutesBefore * 60 * 1000;
}

function groupUpcoming(lessons) {
  const currentTs = now();
  const currentDate = new Date(currentTs);

  return (lessons || [])
    .map((lesson) => {
      if (!lesson?.scheduled_at) return null;
      if (lesson.is_recurring_weekly) {
        const occurrenceStart = getRelevantWeeklyOccurrenceStart(
          lesson,
          currentDate,
        );
        return occurrenceStart
          ? withOccurrenceScheduledAt(lesson, occurrenceStart)
          : null;
      }
      return lesson;
    })
    .filter(Boolean)
    .filter((lesson) => {
      const scheduledTs = new Date(lesson.scheduled_at).getTime();
      if (!Number.isFinite(scheduledTs)) return false;
      const durationMinutes = Number(lesson?.duration_minutes) || 60;
      const endTs = scheduledTs + durationMinutes * 60 * 1000;
      return endTs >= currentTs;
    })
    .sort(
      (a, b) =>
        new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime(),
    );
}

function isPastLesson(lesson) {
  const when = new Date(lesson?.scheduled_at).getTime();
  return Number.isFinite(when) && when <= Date.now();
}

// Only users with an uploaded photo get an image; others show initials.
// The shared helper turns relative paths into absolute backend URLs.
function getAvatarUrl(user) {
  const raw = user?.avatar_url;
  if (typeof raw === "string" && raw.trim()) {
    return resolveAvatarUrl(user);
  }
  return null;
}

function getInitials(user) {
  const base =
    user?.full_name ||
    `${user?.first_name || ""} ${user?.last_name || ""}`.trim() ||
    user?.email ||
    "";

  const cleaned = String(base).trim();
  if (!cleaned) return "?";

  const parts = cleaned
    .replace(/[@._-]+/g, " ")
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) return cleaned.slice(0, 1).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (
    parts[0].slice(0, 1) + parts[parts.length - 1].slice(0, 1)
  ).toUpperCase();
}

function startOfDay(value) {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
}

function isoDayKey(value) {
  const date = startOfDay(value);
  return date.toISOString().slice(0, 10);
}

function getWeekStartMonday(value) {
  const date = startOfDay(value);
  const day = date.getDay();
  const diff = (day + 6) % 7;
  date.setDate(date.getDate() - diff);
  return date;
}

function addDays(value, days) {
  const date = new Date(value);
  date.setDate(date.getDate() + days);
  return date;
}

function makeTimeFormatter(language) {
  const locale = language === "ar" ? "ar-TN" : language || "en";
  return new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

export default function Dashboard() {
  const {
    user,
    effectiveUser,
    effectiveRole,
    accessToken,
    activeStudentId,
    setActiveStudent,
    login,
  } = useAuth();
  const { t, language } = useTranslation();
  const [lessons, setLessons] = useState([]);
  const [students, setStudents] = useState([]);
  const [classGroups, setClassGroups] = useState([]);
  const [catalog, setCatalog] = useState({ subjects: [], featured_packs: [] });
  const [programs, setPrograms] = useState([]);
  const [programsLoading, setProgramsLoading] = useState(false);
  const [programsError, setProgramsError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [exitPassword, setExitPassword] = useState("");
  const [showExitPassword, setShowExitPassword] = useState(false);
  const [exitBusy, setExitBusy] = useState(false);
  const [exitError, setExitError] = useState("");

  const dateTimeFormatter = useMemo(
    () => makeDateTimeFormatter(language),
    [language],
  );

  const formatDate = (value) => formatDateTime(value, dateTimeFormatter);
  const statusLabel = (status) => {
    const key = STATUS_KEYS[status];
    if (key) return t(key);
    return status || "";
  };
  const roleTitleKey = `dashboard.role.${effectiveRole}`;
  const roleTitle =
    t(roleTitleKey) === roleTitleKey
      ? t("dashboard.role.member")
      : t(roleTitleKey);

  useEffect(() => {
    if (!user || !accessToken) {
      return;
    }

    let ignore = false;
    async function load() {
      setLoading(true);
      setError(null);

      try {
        const [lessonsPayload, catalogPayload] = await Promise.all([
          lessonsService.fetchLessons({ accessToken }),
          lessonsService.fetchCatalog(),
        ]);

        setLessons(lessonsPayload.lessons || []);
        setCatalog(catalogPayload || { subjects: [], featured_packs: [] });

        if (
          [
            "admin",
            "superadmin",
            "support",
            "accounting",
            "parent",
            "teacher",
            "student",
          ].includes(user.role)
        ) {
          try {
            const studentsPayload = await studentsService.fetchStudents({
              accessToken,
            });
            if (!ignore) {
              setStudents(studentsPayload.students || []);
            }
          } catch (studentError) {
            if (!ignore && studentError.status !== 403) {
              setError(studentError.message);
            }
          }
        } else {
          setStudents([]);
        }

        if (["parent", "teacher", "student"].includes(user.role)) {
          try {
            const classesPayload = await classesService.fetchClasses({
              accessToken,
            });
            if (!ignore) {
              setClassGroups(classesPayload.classes || []);
            }
          } catch (classesError) {
            if (!ignore && classesError.status !== 403) {
              setError(classesError.message);
            }
            if (!ignore) {
              setClassGroups([]);
            }
          }
        } else {
          setClassGroups([]);
        }
      } catch (loadError) {
        if (!ignore) {
          setError(loadError.message);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      ignore = true;
    };
  }, [user, accessToken]);

  useEffect(() => {
    if (!user || !accessToken || effectiveRole !== "teacher") {
      setPrograms([]);
      return;
    }

    let ignore = false;
    async function loadPrograms() {
      setProgramsLoading(true);
      setProgramsError(null);
      try {
        const payload = await programsService.fetchPrograms({ accessToken });
        if (!ignore) {
          setPrograms(payload.programs || []);
        }
      } catch (programsErr) {
        if (!ignore) {
          setProgramsError(programsErr.message);
        }
      } finally {
        if (!ignore) {
          setProgramsLoading(false);
        }
      }
    }

    loadPrograms();
    return () => {
      ignore = true;
    };
  }, [accessToken, effectiveRole, user]);

  const upcomingLessons = useMemo(
    () => groupUpcoming(lessons).slice(0, 4),
    [lessons],
  );

  const completedLessonsCount = useMemo(
    () => lessons.filter((lesson) => lesson.status === "completed").length,
    [lessons],
  );

  const cancelledLessonsCount = useMemo(
    () => lessons.filter((lesson) => lesson.status === "cancelled").length,
    [lessons],
  );

  const nextLesson = upcomingLessons[0];
  const greetingName = user?.first_name || user?.full_name || user?.email;

  const isAdminRole = ["admin", "superadmin", "support", "accounting"].includes(
    effectiveRole,
  );

  const location = useLocation();

  const { packId } = useParams();

  if (packId && !isAdminRole) {
    return <Navigate to="/dashboard" replace />;
  }

  if (isAdminRole) {
    const legacyTab = new URLSearchParams(location.search).get("tab");
    if (legacyTab) {
      const tab = legacyTab === "lessons" ? "timetable" : legacyTab;
      const to =
        tab === "overview"
          ? "/dashboard"
          : tab === "users"
            ? "/users"
            : tab === "students"
              ? "/admin/students"
              : tab === "timetable"
                ? "/admin/timetable"
                : tab === "pendingTeachers"
                  ? "/admin/pending-teachers"
                  : tab === "classes"
                    ? "/admin/classes"
                    : tab === "packs"
                      ? "/packs"
                      : tab === "payments"
                        ? "/admin/payments"
                        : tab === "teachers"
                          ? "/admin/teachers"
                          : tab === "support"
                            ? "/admin/support"
                            : "/dashboard";
      return <Navigate to={to} replace />;
    }

    if (packId) {
      return <PackDetails packId={packId} />;
    }
    return <Admin forcedTab="overview" />;
  }

  if (effectiveRole === "teacher") {
    return (
      <TeacherDashboard
        user={effectiveUser}
        lessons={lessons}
        students={students}
        classGroups={classGroups}
        upcomingLessons={upcomingLessons}
        loading={loading}
        error={error}
        activeStudentId={activeStudentId}
        setActiveStudent={setActiveStudent}
        programs={programs}
        programsLoading={programsLoading}
        programsError={programsError}
        accessToken={accessToken}
      />
    );
  }

  // Real student accounts should always land on the student dashboard UI.
  if (user?.role === "student") {
    const selfStudent =
      students.find((s) => Number(s.user_id) === Number(user.id)) ||
      students[0] ||
      null;

    return (
      <StudentShell>
        {loading ? (
          <p className="dashboard-muted">{t("dashboard.loading")}</p>
        ) : selfStudent ? (
          <StudentSpace
            student={selfStudent}
            lessons={lessons.filter(
              (lesson) => lesson.student?.id === selfStudent.id,
            )}
            classGroups={classGroups}
          />
        ) : (
          <div className="dashboard-error" role="alert">
            <p>{t("dashboard.studentSpace.errors.profileLoad")}</p>
          </div>
        )}
      </StudentShell>
    );
  }

  if (effectiveRole === "student" && activeStudentId) {
    const activeStudent =
      students.find((s) => s.id === activeStudentId) || null;
    return (
      <StudentShell>
        {showExitConfirm && (
          <div className="modal-backdrop" role="dialog" aria-modal="true">
            <div className="modal-card">
              <h3>{t("dashboard.parent.modal.exitTitle")}</h3>
              <p className="dashboard-muted">
                {t("dashboard.parent.modal.exitBody")}
              </p>
              <form
                className="modal-form"
                onSubmit={async (event) => {
                  event.preventDefault();
                  if (!user?.email) {
                    setExitError(t("authPage.loginError"));
                    return;
                  }
                  setExitBusy(true);
                  setExitError("");
                  try {
                    await login(user.email, exitPassword);
                    setActiveStudent(null);
                    setShowExitConfirm(false);
                    setExitPassword("");
                    setShowExitPassword(false);
                  } catch (authError) {
                    setExitError(t("dashboard.parent.modal.passwordError"));
                  } finally {
                    setExitBusy(false);
                  }
                }}
              >
                <label className="form-field">
                  <span>{t("dashboard.parent.modal.passwordLabel")}</span>
                  <div className="password-wrap">
                    <input
                      type={showExitPassword ? "text" : "password"}
                      value={exitPassword}
                      onChange={(e) => setExitPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowExitPassword((current) => !current)}
                      aria-label={
                        showExitPassword
                          ? t("authPage.hidePassword")
                          : t("authPage.showPassword")
                      }
                      title={
                        showExitPassword
                          ? t("authPage.hidePassword")
                          : t("authPage.showPassword")
                      }
                      disabled={exitBusy}
                    >
                      <i
                        className={`fa-solid ${showExitPassword ? "fa-eye-slash" : "fa-eye"}`}
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </label>
                {exitError && <p className="form-error">{exitError}</p>}
                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn ghost"
                    onClick={() => {
                      if (exitBusy) return;
                      setShowExitConfirm(false);
                      setExitPassword("");
                      setShowExitPassword(false);
                      setExitError("");
                    }}
                  >
                    {t("dashboard.parent.modal.cancel")}
                  </button>
                  <button
                    type="submit"
                    className="btn primary"
                    disabled={exitBusy}
                  >
                    {exitBusy
                      ? t("dashboard.parent.modal.verifying")
                      : t("dashboard.parent.modal.confirm")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        <div className="student-topbar">
          <button
            className="btn ghost"
            onClick={() => setShowExitConfirm(true)}
          >
            {t("dashboard.parent.modal.backToParent")}
          </button>
        </div>
        {loading ? (
          <p className="dashboard-muted">{t("dashboard.loading")}</p>
        ) : activeStudent ? (
          <StudentSpace
            student={activeStudent}
            lessons={lessons.filter(
              (lesson) => lesson.student?.id === activeStudent.id,
            )}
            classGroups={classGroups}
          />
        ) : (
          <div className="dashboard-error" role="alert">
            <p>{t("dashboard.parent.activeStudent.prompt")}</p>
          </div>
        )}
      </StudentShell>
    );
  }

  if (effectiveRole === "parent") {
    return (
      <ParentDashboard
        user={user}
        accessToken={accessToken}
        lessons={lessons}
        students={students}
        classGroups={classGroups}
        catalog={catalog}
        upcomingLessons={upcomingLessons}
        loading={loading}
        error={error}
        activeStudentId={activeStudentId}
        setActiveStudent={setActiveStudent}
      />
    );
  }

  return (
    <div className="dashboard-page">
      <div className="dashboard-shell">
        <section className="dashboard-hero">
          <div className="dashboard-hero__copy">
            <p className="dashboard-hero__role">{roleTitle}</p>
            <h1>{t("dashboard.hero.greeting", { name: greetingName })}</h1>
            <p>{t("dashboard.hero.intro")}</p>
            <div className="dashboard-actions">
              <Link to="/lessons" className="btn primary">
                {t("dashboard.hero.manageLessons")}
              </Link>
            </div>
          </div>
          {nextLesson && (
            <div className="dashboard-hero__card">
              <h3>{t("dashboard.hero.nextLesson")}</h3>
              <p className="lesson-title">{nextLesson.title}</p>
              <dl>
                <div>
                  <dt>{t("dashboard.hero.student")}</dt>
                  <dd>{nextLesson.student.full_name}</dd>
                </div>
                <div>
                  <dt>{t("dashboard.hero.teacher")}</dt>
                  <dd>
                    {nextLesson.teacher.full_name || nextLesson.teacher.email}
                  </dd>
                </div>
                <div>
                  <dt>{t("dashboard.hero.time")}</dt>
                  <dd>{formatDate(nextLesson.scheduled_at)}</dd>
                </div>
                <div>
                  <dt>{t("dashboard.hero.duration")}</dt>
                  <dd>
                    {nextLesson.duration_minutes} {t("lessons.minutes")}
                  </dd>
                </div>
              </dl>
            </div>
          )}
        </section>

        {error && (
          <div className="dashboard-error" role="alert">
            <p>{t("dashboard.errors.loadData", { error })}</p>
          </div>
        )}

        <section className="dashboard-section">
          <div className="dashboard-cards">
            <article className="dashboard-card">
              <p className="dashboard-card__label">
                {t("dashboard.stats.total.label")}
              </p>
              <strong className="dashboard-card__value">
                {lessons.length}
              </strong>
              <span className="dashboard-card__hint">
                {t("dashboard.stats.total.hint")}
              </span>
            </article>
            <article className="dashboard-card">
              <p className="dashboard-card__label">
                {t("dashboard.stats.upcoming.label")}
              </p>
              <strong className="dashboard-card__value">
                {upcomingLessons.length}
              </strong>
              <span className="dashboard-card__hint">
                {t("dashboard.stats.upcoming.hint")}
              </span>
            </article>
            <article className="dashboard-card">
              <p className="dashboard-card__label">
                {t("dashboard.stats.completed.label")}
              </p>
              <strong className="dashboard-card__value">
                {completedLessonsCount}
              </strong>
              <span className="dashboard-card__hint">
                {t("dashboard.stats.completed.hint")}
              </span>
            </article>
            <article className="dashboard-card">
              <p className="dashboard-card__label">
                {t("dashboard.stats.cancelled.label")}
              </p>
              <strong className="dashboard-card__value">
                {cancelledLessonsCount}
              </strong>
              <span className="dashboard-card__hint">
                {t("dashboard.stats.cancelled.hint")}
              </span>
            </article>
          </div>
        </section>

        <section className="dashboard-section">
          <header className="dashboard-section__head">
            <div>
              <h2>{t("dashboard.upcoming.title")}</h2>
              <p>{t("dashboard.upcoming.subtitle")}</p>
            </div>
            <Link to="/lessons" className="btn ghost">
              {t("dashboard.upcoming.viewAll")}
            </Link>
          </header>
          {loading ? (
            <p className="dashboard-muted">{t("dashboard.upcoming.loading")}</p>
          ) : upcomingLessons.length ? (
            <div className="dashboard-list">
              {upcomingLessons.map((lesson) => (
                <article className="dashboard-list__item" key={lesson.id}>
                  <div>
                    <h3>{lesson.title}</h3>
                    <p className="dashboard-muted">
                      {lesson.student.full_name} •{" "}
                      {lesson.teacher.full_name || lesson.teacher.email}
                    </p>
                  </div>
                  <div className="dashboard-list__meta">
                    <span>{formatDate(lesson.scheduled_at)}</span>
                    <span className={`status status--${lesson.status}`}>
                      {statusLabel(lesson.status)}
                    </span>
                    {lesson.meeting_url && isWithinMeetingJoinWindow(lesson) ? (
                      <a
                        href={lesson.meeting_url}
                        target="_blank"
                        rel="noreferrer"
                        className="dashboard-list__link"
                      >
                        {t("lessons.joinMeeting")}
                      </a>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <p className="dashboard-muted">{t("dashboard.upcoming.empty")}</p>
          )}
        </section>

        {(user?.role === "admin" || user?.role === "parent") && (
          <section className="dashboard-section">
            <header className="dashboard-section__head">
              <div>
                <h2>{t("dashboard.students.title")}</h2>
                <p>{t("dashboard.students.subtitle")}</p>
              </div>
            </header>
            {students.length ? (
              <div className="dashboard-grid">
                {students.slice(0, 6).map((student) => (
                  <article className="dashboard-mini-card" key={student.id}>
                    <h3>{student.full_name}</h3>
                    <p className="dashboard-muted">
                      {t("dashboard.students.id")}: {student.id}
                    </p>
                    {student.date_of_birth && (
                      <p className="dashboard-muted">
                        {t("dashboard.students.dob")}: {student.date_of_birth}
                      </p>
                    )}
                  </article>
                ))}
              </div>
            ) : (
              <p className="dashboard-muted">{t("dashboard.students.empty")}</p>
            )}
          </section>
        )}
      </div>
    </div>
  );
}

function TeacherDashboard({
  user,
  lessons,
  students,
  upcomingLessons,
  loading,
  error,
  programs,
  programsLoading,
  programsError,
  accessToken,
}) {
  const { t, language } = useTranslation();
  const [search, setSearch] = useState("");
  const dateTimeFormatter = useMemo(
    () => makeDateTimeFormatter(language),
    [language],
  );
  const formatDate = (value) => formatDateTime(value, dateTimeFormatter);
  const statusLabel = (status) => {
    const key = STATUS_KEYS[status];
    if (key) return t(key);
    return status || "";
  };
  const greetingName = user?.first_name || user?.full_name || user?.email;

  const sessionLessons = useMemo(
    () => dedupeTeacherSessions(lessons),
    [lessons],
  );

  const upcomingSessions = useMemo(
    () => groupUpcoming(sessionLessons),
    [sessionLessons],
  );

  const activeStudentsCount = useMemo(() => {
    const ids = new Set();
    lessons.forEach((lesson) => {
      if (lesson.student?.id) ids.add(lesson.student.id);
    });
    return ids.size;
  }, [lessons]);

  const hoursGiven = useMemo(() => {
    const minutes = sessionLessons.reduce(
      (total, lesson) => total + (lesson.duration_minutes || 0),
      0,
    );
    return (minutes / 60).toFixed(1);
  }, [sessionLessons]);

  const completedLessonsCount = useMemo(
    () =>
      sessionLessons.filter((lesson) => lesson.status === "completed").length,
    [sessionLessons],
  );

  const cancelledLessonsCount = useMemo(
    () =>
      sessionLessons.filter((lesson) => lesson.status === "cancelled").length,
    [sessionLessons],
  );

  const completionRate = useMemo(() => {
    // Only sessions that have already taken place; upcoming ones aren't missed.
    const past = sessionLessons.filter(isPastLesson);
    if (!past.length) return 0;
    const completed = past.filter((lesson) => lesson.status === "completed").length;
    return Math.round((completed / past.length) * 100);
  }, [sessionLessons]);

  const performanceProgress = useMemo(() => {
    return {
      percent: completionRate,
      text: `${completionRate}%`,
    };
  }, [completionRate]);

  const activityData = useMemo(() => {
    const locale = language === "ar" ? "ar-TN" : language || "en";
    const weekdayFormatter = new Intl.DateTimeFormat(locale, {
      weekday: "short",
    });

    const today = startOfDay(new Date());
    const days = Array.from({ length: 7 }, (_, index) =>
      addDays(today, -(6 - index)),
    );

    const bucket = new Map(days.map((d) => [isoDayKey(d), 0]));
    for (const lesson of sessionLessons || []) {
      const key = isoDayKey(lesson.scheduled_at);
      if (bucket.has(key)) {
        bucket.set(key, (bucket.get(key) || 0) + 1);
      }
    }

    return days.map((date) => {
      const key = isoDayKey(date);
      return {
        name: weekdayFormatter.format(date),
        lessons: bucket.get(key) || 0,
      };
    });
  }, [sessionLessons, language]);

  const topStudents = useMemo(() => {
    const counts = new Map();
    for (const lesson of lessons || []) {
      const student = lesson.student;
      if (!student?.id) continue;
      const current = counts.get(student.id) || {
        id: student.id,
        name: student.full_name || "—",
        count: 0,
      };
      current.count += 1;
      counts.set(student.id, current);
    }

    return Array.from(counts.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, 4);
  }, [lessons]);

  const recentLessons = useMemo(() => {
    const sorted = [...(sessionLessons || [])].sort(
      (a, b) =>
        new Date(b.scheduled_at).getTime() - new Date(a.scheduled_at).getTime(),
    );
    const filtered = sorted.filter(
      (lesson) =>
        lesson.status === "completed" || lesson.status === "cancelled",
    );
    if (!search.trim()) {
      return filtered.slice(0, 4);
    }
    const term = search.trim().toLowerCase();
    return filtered
      .filter((lesson) => {
        const title = String(lesson.title || "").toLowerCase();
        const who = String(
          lesson.class_group?.name || lesson.student?.full_name || "",
        ).toLowerCase();
        return title.includes(term) || who.includes(term);
      })
      .slice(0, 4);
  }, [sessionLessons, search]);

  const upcomingFiltered = useMemo(() => {
    if (!search.trim()) {
      return upcomingSessions;
    }
    const term = search.trim().toLowerCase();
    return upcomingSessions.filter((lesson) => {
      const title = String(lesson.title || "").toLowerCase();
      const who = String(
        lesson.class_group?.name || lesson.student?.full_name || "",
      ).toLowerCase();
      return title.includes(term) || who.includes(term);
    });
  }, [upcomingSessions, search]);

  return (
    <div className="dashboard-page teacher-dashboard">
      <div className="teacher-dashboard__shell">
        <header className="teacher-dashboard__top">
          <div className="teacher-dashboard__greeting">
            <h1>{t("dashboard.teacher.greeting", { name: greetingName })}</h1>
            <p className="dashboard-muted">{t("dashboard.teacher.intro")}</p>
          </div>

          <div className="teacher-dashboard__tools">
            <div className="teacher-dashboard__search">
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t("dashboard.searchPlaceholder")}
                aria-label={t("nav.searchLabel")}
              />
            </div>
            <div className="teacher-dashboard__actions">
              <Link to="/lessons" className="btn ghost">
                {t("dashboard.teacher.actions.schedule")}
              </Link>
              <Link to="/lessons/new" className="btn primary">
                {t("dashboard.teacher.actions.newLesson")}
              </Link>
            </div>
          </div>
        </header>

        {error && (
          <div className="dashboard-error" role="alert">
            <p>{t("dashboard.errors.loadData", { error })}</p>
          </div>
        )}

        <section className="teacher-dashboard__overview">
          <div className="teacher-statCard teacher-statCard--mint">
            <div className="teacher-statCard__label">
              {t("dashboard.teacher.stats.activeStudents.label")}
            </div>
            <div className="teacher-statCard__value">{activeStudentsCount}</div>
          </div>

          <div className="teacher-statCard teacher-statCard--sand">
            <div className="teacher-statCard__label">
              {t("dashboard.teacher.stats.completed.label")}
            </div>
            <div className="teacher-statCard__value">
              {completedLessonsCount}
            </div>
          </div>

          <div className="teacher-statCard teacher-statCard--sky">
            <div className="teacher-statCard__label">
              {t("dashboard.teacher.stats.hoursGiven.label")}
            </div>
            <div className="teacher-statCard__value">{hoursGiven}</div>
          </div>

          <div className="teacher-card teacher-performance">
            <div className="teacher-card__head">
              <h2>{t("dashboard.teacher.performance.title")}</h2>
            </div>
            <div className="teacher-performance__body">
              <div
                className="teacher-performance__ring"
                style={{ "--progress": `${performanceProgress.percent}%` }}
                aria-hidden="true"
              >
                <div className="teacher-performance__value">
                  {performanceProgress.text}
                </div>
              </div>
              <div className="teacher-performance__legend">
                <div className="teacher-performance__legendRow">
                  <span className="teacher-dot teacher-dot--actual" />
                  <span>{t("dashboard.teacher.performance.completed")}</span>
                  <strong>{completedLessonsCount}</strong>
                </div>
                <div className="teacher-performance__legendRow">
                  <span className="teacher-dot teacher-dot--expected" />
                  <span>{t("dashboard.teacher.performance.cancelled")}</span>
                  <strong>{cancelledLessonsCount}</strong>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="teacher-dashboard__programs">
          <article className="teacher-card teacher-programs">
            <div className="teacher-card__head">
              <h2>{t("dashboard.teacher.programs.title")}</h2>
            </div>
            {programsError ? (
              <p className="dashboard-muted">{programsError}</p>
            ) : programsLoading ? (
              <p className="dashboard-muted">
                {t("dashboard.teacher.programs.loading")}
              </p>
            ) : programs.length ? (
              <div className="teacher-programs__list">
                {programs.map((program) => (
                  <Link
                    className="teacher-programs__row teacher-programs__row--link"
                    key={program.slug || program.id}
                    to={`/programs/${program.slug || program.id}?edit=1`}
                  >
                    <div className="teacher-programs__info">
                      <div className="teacher-programs__title">
                        {program.title}
                      </div>
                    </div>
                    <span className="teacher-link">
                      {t("dashboard.teacher.programs.edit")}
                    </span>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="dashboard-muted">
                {t("dashboard.teacher.programs.empty")}
              </p>
            )}
          </article>
        </section>

        <section className="teacher-dashboard__grid">
          <article className="teacher-card teacher-activity">
            <div className="teacher-card__head">
              <h2>{t("dashboard.teacher.weeklyActivity.title")}</h2>
              <span className="teacher-card__hint">
                {t("dashboard.teacher.weeklyActivity.hint")}
              </span>
            </div>
            <div className="teacher-chart">
              <ResponsiveContainer width="100%" height={240}>
                <LineChart
                  data={activityData}
                  margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                >
                  <CartesianGrid
                    stroke="rgba(15, 35, 78, 0.08)"
                    strokeDasharray="3 3"
                  />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: "rgba(15, 35, 78, 0.65)", fontSize: 12 }}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fill: "rgba(15, 35, 78, 0.65)", fontSize: 12 }}
                  />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="lessons"
                    stroke="#F7AB16"
                    strokeWidth={3}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </article>

          <article className="teacher-card teacher-top">
            <div className="teacher-card__head">
              <h2>{t("dashboard.teacher.topStudents.title")}</h2>
            </div>
            {topStudents.length ? (
              <div className="teacher-topList">
                {topStudents.map((student) => (
                  <div className="teacher-topRow" key={student.id}>
                    <div className="teacher-topRow__title">{student.name}</div>
                    <div className="teacher-topRow__meta">
                      <span className="status status--scheduled">
                        {t("dashboard.teacher.topStudents.lessons", {
                          count: student.count,
                        })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="dashboard-muted">
                {t("dashboard.teacher.topStudents.empty")}
              </p>
            )}
          </article>
        </section>

        <section className="teacher-dashboard__grid teacher-dashboard__grid--bottom">
          <article className="teacher-card teacher-transactions">
            <div className="teacher-card__head">
              <h2>{t("dashboard.teacher.updates.title")}</h2>
            </div>
            {loading ? (
              <p className="dashboard-muted">
                {t("dashboard.teacher.schedule.loading")}
              </p>
            ) : recentLessons.length ? (
              <div className="teacher-events">
                {recentLessons.map((lesson) => (
                  <div className="teacher-event" key={lesson.id}>
                    <div className="teacher-event__main">
                      <div className="teacher-event__title">{lesson.title}</div>
                      <div className="teacher-event__meta">
                        {lesson.class_group?.name ||
                          lesson.student?.full_name ||
                          "—"}{" "}
                        • {formatDate(lesson.scheduled_at)}
                      </div>
                    </div>
                    <span className={`status status--${lesson.status}`}>
                      {statusLabel(lesson.status)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="dashboard-muted">
                {t("dashboard.teacher.updates.empty")}
              </p>
            )}
          </article>

          <article className="teacher-card teacher-upcoming">
            <div className="teacher-card__head">
              <h2>{t("dashboard.teacher.schedule.title")}</h2>
              <Link to="/lessons" className="teacher-link">
                {t("dashboard.teacher.schedule.view")}
              </Link>
            </div>
            {loading ? (
              <p className="dashboard-muted">
                {t("dashboard.teacher.schedule.loading")}
              </p>
            ) : upcomingFiltered.length ? (
              <div className="teacher-events">
                {upcomingFiltered.slice(0, 4).map((lesson) => (
                  <div className="teacher-event" key={lesson.id}>
                    <div className="teacher-event__main">
                      <div className="teacher-event__title">{lesson.title}</div>
                      <div className="teacher-event__meta">
                        {lesson.class_group?.name ||
                          lesson.student?.full_name ||
                          "—"}{" "}
                        • {formatDate(lesson.scheduled_at)}
                      </div>
                    </div>
                    <div className="teacher-event__side">
                      <span className={`status status--${lesson.status}`}>
                        {statusLabel(lesson.status)}
                      </span>
                      {lesson.meeting_url ? (
                        isWithinMeetingJoinWindow(lesson) ? (
                          <a
                            href={lesson.meeting_url}
                            target="_blank"
                            rel="noreferrer"
                            className="teacher-event__join"
                          >
                            {t("lessons.joinMeeting")}
                          </a>
                        ) : (
                          <span
                            className="teacher-event__join teacher-event__join--disabled"
                            aria-disabled="true"
                          >
                            {t("lessons.joinMeetingSoon")}
                          </span>
                        )
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="dashboard-muted">
                {t("dashboard.teacher.schedule.empty")}
              </p>
            )}
          </article>
        </section>
      </div>
    </div>
  );
}

function ParentDashboard({
  user,
  accessToken,
  students,
  lessons,
  classGroups,
  catalog,
  upcomingLessons,
  loading,
  error,
  activeStudentId,
  setActiveStudent,
}) {
  const { t, language } = useTranslation();
  const dateTimeFormatter = useMemo(
    () => makeDateTimeFormatter(language),
    [language],
  );
  const timeFormatter = useMemo(() => makeTimeFormatter(language), [language]);
  const formatDate = (value) => formatDateTime(value, dateTimeFormatter);
  const formatTime = (value) => {
    if (!value) return "";
    try {
      return timeFormatter.format(new Date(value));
    } catch (error) {
      return "";
    }
  };
  const statusLabel = (status) => {
    const key = STATUS_KEYS[status];
    if (key) return t(key);
    return status || "";
  };
  const greetingName = user?.first_name || user?.full_name || user?.email;

  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationsError, setNotificationsError] = useState("");
  const [markReadBusyId, setMarkReadBusyId] = useState(null);
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);
  const [activeNotificationId, setActiveNotificationId] = useState(null);

  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!accessToken) return;
    let ignore = false;

    async function loadNotifications() {
      setNotificationsLoading(true);
      setNotificationsError("");
      try {
        const payload = await lessonsService.fetchParentLessonNotifications({
          accessToken,
        });
        if (!ignore) {
          setNotifications(payload.notifications || []);
        }
      } catch (err) {
        if (!ignore) {
          setNotificationsError(err.message);
        }
      } finally {
        if (!ignore) {
          setNotificationsLoading(false);
        }
      }
    }

    loadNotifications();
    return () => {
      ignore = true;
    };
  }, [accessToken]);

  const avatarUrl = useMemo(() => getAvatarUrl(user), [user]);
  const avatarInitials = useMemo(() => getInitials(user), [user]);

  const unreadCount = useMemo(
    () => (notifications || []).filter((n) => !n.is_read).length,
    [notifications],
  );

  const notificationStatusLabel = (status) => {
    const key = {
      cancelled: "dashboard.parent.notifications.status.cancelled",
      postponed: "dashboard.parent.notifications.status.postponed",
      updated: "dashboard.parent.notifications.status.updated",
    }[status];
    if (!key) return status || "";
    const resolved = t(key);
    return resolved === key ? status || "" : resolved;
  };

  const handleMarkNotificationRead = async (notificationId) => {
    if (!notificationId || !accessToken || markReadBusyId) return;
    setMarkReadBusyId(notificationId);
    try {
      const payload = await lessonsService.markParentLessonNotificationRead({
        accessToken,
        notificationId,
      });
      setNotifications((current) =>
        (current || []).map((item) =>
          item.id === notificationId ? payload : item,
        ),
      );
    } catch (err) {
      setNotificationsError(err.message);
    } finally {
      setMarkReadBusyId(null);
    }
  };

  const openNotificationModal = (notificationId) => {
    if (!notificationId) return;
    setActiveNotificationId(notificationId);
    setNotificationModalOpen(true);
    setNotificationsOpen(false);

    const item = (notifications || []).find(
      (n) => String(n.id) === String(notificationId),
    );
    if (item && !item.is_read) {
      handleMarkNotificationRead(item.id);
    }
  };

  const closeNotificationModal = () => {
    setNotificationModalOpen(false);
    setActiveNotificationId(null);
  };

  const activeNotification = useMemo(() => {
    if (!activeNotificationId) return null;
    return (notifications || []).find(
      (item) => String(item.id) === String(activeNotificationId),
    );
  }, [notifications, activeNotificationId]);

  const completionRate = useMemo(() => {
    const past = lessons.filter(isPastLesson);
    if (!past.length) return 0;
    const completed = past.filter((lesson) => lesson.status === "completed").length;
    return Math.round((completed / past.length) * 100);
  }, [lessons]);

  const thisWeekDays = useMemo(() => {
    const weekStart = getWeekStartMonday(new Date());
    return Array.from({ length: 7 }, (_, index) => addDays(weekStart, index));
  }, []);

  const weekLessonsByDay = useMemo(() => {
    const start = thisWeekDays[0];
    const end = addDays(thisWeekDays[thisWeekDays.length - 1], 1);
    const startTs = start.getTime();
    const endTs = end.getTime();

    const map = new Map();
    thisWeekDays.forEach((day) => map.set(isoDayKey(day), []));

    lessons
      .filter((lesson) => {
        if (!lesson?.scheduled_at) return false;
        const ts = new Date(lesson.scheduled_at).getTime();
        return ts >= startTs && ts < endTs;
      })
      .sort(
        (a, b) =>
          new Date(a.scheduled_at).getTime() -
          new Date(b.scheduled_at).getTime(),
      )
      .forEach((lesson) => {
        const key = isoDayKey(lesson.scheduled_at);
        if (!map.has(key)) {
          map.set(key, []);
        }
        map.get(key).push(lesson);
      });

    return map;
  }, [lessons, thisWeekDays]);

  const reminders = useMemo(
    () => upcomingLessons.slice(0, 2),
    [upcomingLessons],
  );
  const homeworkItems = useMemo(
    () => upcomingLessons.slice(0, 5),
    [upcomingLessons],
  );

  const classesByStudentId = useMemo(() => {
    const map = new Map();
    (classGroups || []).forEach((group) => {
      (group.student_ids || []).forEach((studentId) => {
        if (!map.has(studentId)) map.set(studentId, []);
        map.get(studentId).push(group);
      });
    });
    return map;
  }, [classGroups]);

  const listFormatter = useMemo(() => {
    const locale = language === "ar" ? "ar-TN" : language || "en";
    try {
      return new Intl.ListFormat(locale, {
        style: "short",
        type: "conjunction",
      });
    } catch (error) {
      return null;
    }
  }, [language]);

  return (
    <div className="dashboard-page parent-dashboard">
      <div className="dashboard-shell parent-dashboard__shell">
        <header className="parent-dashboard__topbar">
          <div className="parent-dashboard__profile">
            <div className="parent-dashboard__avatar" aria-hidden="true">
              {avatarUrl ? (
                <img src={avatarUrl} alt="" />
              ) : (
                <span>{avatarInitials}</span>
              )}
            </div>
            <div className="parent-dashboard__profileText">
              <p className="parent-dashboard__greeting">
                {t("dashboard.parent.greeting", { name: greetingName })}
              </p>
              <p className="dashboard-muted">{t("dashboard.parent.intro")}</p>
            </div>
          </div>

          <div className="parent-dashboard__search" role="search">
            <label className="sr-only" htmlFor="parent-dashboard-search">
              {t("nav.searchLabel")}
            </label>
            <input
              id="parent-dashboard-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("nav.searchPlaceholder")}
              type="search"
              autoComplete="off"
            />
          </div>

          <div className="parent-dashboard__topActions">
            {students.length > 0 && (
              <label className="parent-dashboard__childSelect">
                <span className="sr-only">
                  {t("dashboard.parent.widgets.studentModeLabel")}
                </span>
                <select
                  value={""}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!value) return;
                    setActiveStudent(Number(value));
                  }}
                >
                  <option value="">
                    {t("dashboard.parent.widgets.studentModePlaceholder")}
                  </option>
                  {students.map((student) => (
                    <option key={student.id} value={student.id}>
                      {student.full_name}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <div className="parent-dashboard__bellWrap">
              <button
                type="button"
                className="parent-dashboard__bell"
                aria-label={t("dashboard.parent.widgets.notifications")}
                onClick={() => setNotificationsOpen((current) => !current)}
              >
                {unreadCount > 0 ? (
                  <span
                    className="parent-dashboard__bellBadge"
                    aria-hidden="true"
                  >
                    {unreadCount}
                  </span>
                ) : null}
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2Zm6-6V11a6 6 0 1 0-12 0v5L4 18v1h16v-1l-2-2Z"
                    fill="currentColor"
                  />
                </svg>
              </button>

              {notificationsOpen ? (
                <section
                  className="parent-dashboard__notifications parent-dashboard__notificationsDropdown"
                  aria-label={t("dashboard.parent.widgets.notifications")}
                >
                  {notificationsError ? (
                    <div className="dashboard-error" role="alert">
                      <p>{notificationsError}</p>
                    </div>
                  ) : null}

                  {notificationsLoading ? (
                    <p className="dashboard-muted">{t("common.loading")}</p>
                  ) : notifications.length ? (
                    <div className="parent-dashboard__notificationsList">
                      {notifications.map((item) => {
                        const status = item.status;
                        const title = item.lesson?.title || "";
                        const scheduledAt = item.lesson?.scheduled_at;
                        const className = item.class_group?.name;
                        const studentName = item.student?.full_name;
                        const affectedStudents = Array.isArray(item.students)
                          ? item.students
                          : [];
                        const affectedNames = affectedStudents
                          .map((s) => s?.full_name)
                          .filter(Boolean);
                        const affectedLabel = affectedNames.length
                          ? listFormatter
                            ? listFormatter.format(affectedNames)
                            : affectedNames.join(", ")
                          : "";

                        const metaLeft = className
                          ? affectedLabel
                            ? `${className} • ${affectedLabel}`
                            : className
                          : studentName || "";

                        return (
                          <article
                            key={item.id}
                            className={`parent-dashboard__notificationItem ${
                              item.is_read ? "is-read" : "is-unread"
                            }`}
                          >
                            <button
                              type="button"
                              className="parent-dashboard__notificationSummary"
                              onClick={() => openNotificationModal(item.id)}
                            >
                              <span className="parent-dashboard__notificationStatus">
                                {notificationStatusLabel(status) || status}
                              </span>
                              <span className="parent-dashboard__notificationTitle">
                                {title}
                              </span>
                              <span className="parent-dashboard__notificationMeta">
                                {metaLeft ? `${metaLeft} • ` : ""}
                                {scheduledAt ? formatDate(scheduledAt) : ""}
                              </span>
                            </button>
                          </article>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="dashboard-muted">
                      {t("dashboard.parent.notifications.empty")}
                    </p>
                  )}
                </section>
              ) : null}
            </div>
          </div>
        </header>

        {notificationModalOpen && activeNotification ? (
          <div
            className="modal-backdrop"
            role="dialog"
            aria-modal="true"
            aria-label={t("dashboard.parent.widgets.notifications")}
          >
            <div className="modal-card">
              <div>
                <div className="parent-dashboard__notificationStatus">
                  {notificationStatusLabel(activeNotification.status) ||
                    activeNotification.status}
                </div>
                <div className="parent-dashboard__notificationTitle">
                  {activeNotification.lesson?.title || ""}
                </div>
                <div className="parent-dashboard__notificationMeta">
                  {activeNotification.class_group?.name
                    ? activeNotification.class_group.name
                    : activeNotification.student?.full_name || ""}
                  {activeNotification.lesson?.scheduled_at
                    ? ` • ${formatDate(activeNotification.lesson.scheduled_at)}`
                    : ""}
                </div>
              </div>

              {activeNotification.message ? (
                <p>{activeNotification.message}</p>
              ) : null}

              {Array.isArray(activeNotification.students) &&
              activeNotification.students.length ? (
                <div className="parent-dashboard__notificationMeta">
                  {(() => {
                    const affectedNames = (activeNotification.students || [])
                      .map((s) => s?.full_name)
                      .filter(Boolean);
                    if (!affectedNames.length) return null;
                    const label = listFormatter
                      ? listFormatter.format(affectedNames)
                      : affectedNames.join(", ");
                    return label;
                  })()}
                </div>
              ) : null}

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn ghost"
                  onClick={closeNotificationModal}
                >
                  {t("sidebar.close")}
                </button>
                {!activeNotification.is_read ? (
                  <button
                    type="button"
                    className="btn primary"
                    disabled={markReadBusyId === activeNotification.id}
                    onClick={() =>
                      handleMarkNotificationRead(activeNotification.id)
                    }
                  >
                    {t("dashboard.parent.notifications.markRead")}
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}

        {error && (
          <div className="dashboard-error" role="alert">
            <p>{t("dashboard.errors.loadData", { error })}</p>
          </div>
        )}

        <div className="parent-dashboard__grid">
          <section className="parent-dashboard__week" aria-label="week">
            <header className="parent-card__head">
              <div>
                <h2>{t("dashboard.parent.actions.weekly")}</h2>
                <p className="dashboard-muted">
                  {t("dashboard.parent.widgets.weekSubtitle")}
                </p>
              </div>
              <Link to="/lessons" className="btn ghost">
                {t("dashboard.upcoming.viewAll")}
              </Link>
            </header>
            <div className="week-mini">
              <div
                className="week-mini__tabs"
                role="tablist"
                aria-label="weekdays"
              >
                {thisWeekDays.map((day) => {
                  const key = isoDayKey(day);
                  const isToday = key === isoDayKey(new Date());
                  const locale = language === "ar" ? "ar-TN" : language || "en";
                  const label = day.toLocaleDateString(locale, {
                    weekday: "short",
                  });
                  return (
                    <div
                      key={key}
                      className={`week-mini__tab ${
                        isToday ? "week-mini__tab--active" : ""
                      }`}
                      role="tab"
                      aria-selected={isToday ? "true" : "false"}
                      title={day.toLocaleDateString(locale, {
                        dateStyle: "medium",
                      })}
                    >
                      {label}
                    </div>
                  );
                })}
              </div>

              <div className="week-mini__cols" role="grid">
                {thisWeekDays.map((day, colIndex) => {
                  const key = isoDayKey(day);
                  const items = weekLessonsByDay.get(key) || [];
                  return (
                    <div
                      className="week-mini__col"
                      key={key}
                      role="row"
                      aria-label={key}
                    >
                      {items.length ? (
                        items.slice(0, 5).map((lesson) => (
                          <div
                            key={lesson.id}
                            className={`week-mini__event week-mini__event--${
                              colIndex % 3
                            }`}
                            title={lesson.title}
                          >
                            <div className="week-mini__eventTime">
                              {formatTime(lesson.scheduled_at)}
                            </div>
                            <div className="week-mini__eventTitle">
                              {lesson.title}
                            </div>
                            {lesson.student?.full_name ? (
                              <div className="week-mini__eventSub">
                                {lesson.student.full_name}
                              </div>
                            ) : null}
                          </div>
                        ))
                      ) : (
                        <div className="week-mini__empty" aria-hidden="true">
                          —
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {students.length > 0 && (
            <section className="parent-card" aria-label="classes">
              <header className="parent-card__head">
                <div>
                  <h2>{t("dashboard.parent.classes.title")}</h2>
                  <p className="dashboard-muted">
                    {t("dashboard.parent.classes.subtitle")}
                  </p>
                </div>
                <Link to="/lessons" className="btn ghost">
                  {t("dashboard.upcoming.viewAll")}
                </Link>
              </header>
              <div style={{ display: "grid", gap: "0.5rem" }}>
                {students.map((student) => {
                  const groups = classesByStudentId.get(student.id) || [];
                  const groupNames = groups.map((g) => g.name).filter(Boolean);
                  const label = groupNames.length
                    ? listFormatter
                      ? listFormatter.format(groupNames)
                      : groupNames.join(", ")
                    : "-";
                  return (
                    <div
                      key={student.id}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: "1rem",
                      }}
                    >
                      <span>{student.full_name}</span>
                      <span className="dashboard-muted">{label}</span>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          <aside className="parent-dashboard__side">
            <section className="parent-card" aria-label="reminders">
              <header className="parent-card__head">
                <h2>{t("dashboard.parent.widgets.reminders")}</h2>
                <Link to="/lessons" className="btn ghost">
                  {t("dashboard.upcoming.viewAll")}
                </Link>
              </header>
              {loading ? (
                <p className="dashboard-muted">
                  {t("dashboard.upcoming.loading")}
                </p>
              ) : reminders.length ? (
                <div className="reminders-list">
                  {reminders.map((lesson) => (
                    <div className="reminder" key={lesson.id}>
                      <div className="reminder__date">
                        <strong>
                          {new Date(lesson.scheduled_at).toLocaleDateString(
                            language === "ar" ? "ar-TN" : language || "en",
                            { day: "2-digit", month: "short" },
                          )}
                        </strong>
                      </div>
                      <div className="reminder__body">
                        <p className="reminder__title">{lesson.title}</p>
                        <p className="dashboard-muted">
                          {lesson.student?.full_name || ""}
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        aria-label={t(
                          "dashboard.parent.widgets.reminderCheckbox",
                        )}
                        disabled
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="dashboard-muted">
                  {t("dashboard.upcoming.empty")}
                </p>
              )}
            </section>

            {null}
          </aside>

          <section
            className="parent-card parent-dashboard__stats"
            aria-label="stats"
          >
            <header className="parent-card__head">
              <h2>{t("dashboard.parent.widgets.monthStats")}</h2>
              <span className="dashboard-muted">
                {t("dashboard.parent.widgets.monthStatsHint")}
              </span>
            </header>
            <div className="month-stats">
              <div className="month-stat">
                <p className="month-stat__label">
                  {t("dashboard.parent.widgets.children")}
                </p>
                <p className="month-stat__value">{students.length}</p>
              </div>
              <div className="month-stat">
                <p className="month-stat__label">
                  {t("dashboard.stats.completed.label")}
                </p>
                <p className="month-stat__value">{completionRate}%</p>
                <div className="progress-bar">
                  <span
                    className="progress-bar__fill"
                    style={{ width: `${completionRate}%` }}
                  />
                </div>
              </div>
              <div className="month-stat">
                <p className="month-stat__label">
                  {t("dashboard.stats.upcoming.label")}
                </p>
                <p className="month-stat__value">{upcomingLessons.length}</p>
              </div>
            </div>
          </section>

          <section
            className="parent-card parent-dashboard__homework"
            aria-label="homework"
          >
            <header className="parent-card__head">
              <div>
                <h2>{t("dashboard.parent.widgets.homework")}</h2>
                <p className="dashboard-muted">
                  {t("dashboard.parent.widgets.homeworkHint")}
                </p>
              </div>
              <Link
                to="/students?add=1"
                className="btn primary"
                onClick={() => setActiveStudent(null)}
              >
                {t("dashboard.parent.children.addChild")}
              </Link>
            </header>

            {loading ? (
              <p className="dashboard-muted">
                {t("dashboard.parent.followup.loading")}
              </p>
            ) : homeworkItems.length ? (
              <div className="homework-list">
                {homeworkItems.map((lesson) => (
                  <div className="homework-row" key={lesson.id}>
                    <div className="homework-row__main">
                      <p className="homework-row__title">{lesson.title}</p>
                      <p className="dashboard-muted">
                        {lesson.student?.full_name} •{" "}
                        {lesson.teacher?.full_name || lesson.teacher?.email}
                      </p>
                    </div>
                    <div className="homework-row__meta">
                      <span className="dashboard-muted">
                        {formatDate(lesson.scheduled_at)}
                      </span>
                      <span className={`status status--${lesson.status}`}>
                        {statusLabel(lesson.status)}
                      </span>
                      {lesson.meeting_url &&
                      isWithinMeetingJoinWindow(lesson) ? (
                        <a
                          href={lesson.meeting_url}
                          target="_blank"
                          rel="noreferrer"
                          className="homework-row__link"
                        >
                          {t("lessons.joinMeeting")}
                        </a>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="dashboard-muted">
                {t("dashboard.parent.followup.empty")}
              </p>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function StudentSpace({ student, lessons, classGroups }) {
  const { accessToken, user, effectiveRole, activeStudentId } = useAuth();
  const { t, language } = useTranslation();
  const tr = (key, options) => t(`studentHome.${key}`, options);

  const timeFormatter = useMemo(() => makeTimeFormatter(language), [language]);
  const dayFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(language || undefined, {
        weekday: "long",
        day: "numeric",
        month: "long",
      }),
    [language],
  );
  const shortDateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(language || undefined, {
        day: "numeric",
        month: "short",
      }),
    [language],
  );

  // "Today", "Tomorrow" or the full date.
  const dayLabel = (date) => {
    const today = startOfDay(new Date());
    const day = startOfDay(date);
    const diff = Math.round((day - today) / 86400000);
    if (diff === 0) return tr("lessons.today");
    if (diff === 1) return tr("lessons.tomorrow");
    return dayFormatter.format(date);
  };

  const [homeworks, setHomeworks] = useState([]);
  const [homeworksLoading, setHomeworksLoading] = useState(false);

  useEffect(() => {
    const inStudentMode =
      effectiveRole === "student" || Boolean(activeStudentId);
    if (!accessToken || !inStudentMode || !student?.id) return undefined;
    let ignore = false;
    setHomeworksLoading(true);
    homeworksService
      .fetchHomeworks({
        accessToken,
        // Parents (student mode) must say which child.
        params: user?.role === "parent" ? { student_id: student.id } : {},
      })
      .then((payload) => {
        if (!ignore) {
          setHomeworks(Array.isArray(payload?.homeworks) ? payload.homeworks : []);
        }
      })
      .catch(() => {
        if (!ignore) setHomeworks([]);
      })
      .finally(() => {
        if (!ignore) setHomeworksLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [accessToken, activeStudentId, effectiveRole, student?.id, user?.role]);

  const firstName =
    String(student?.first_name || student?.full_name || "").trim().split(/\s+/)[0] ||
    student?.full_name ||
    "";

  const classNames = useMemo(
    () =>
      (classGroups || [])
        .filter((g) => (g.student_ids || []).includes(student.id))
        .map((g) => g.name)
        .filter(Boolean),
    [classGroups, student.id],
  );

  const upcoming = useMemo(
    () =>
      groupUpcoming(
        (lessons || []).filter((lesson) => lesson.status !== "cancelled"),
      ).slice(0, 4),
    [lessons],
  );
  const [nextLesson, ...laterLessons] = upcoming;

  // Homework still to do first (soonest due date), then the ones already sent.
  const homeworkItems = useMemo(() => {
    const dueTs = (hw) => {
      const ts = hw?.due_at ? new Date(hw.due_at).getTime() : NaN;
      return Number.isFinite(ts) ? ts : Infinity;
    };
    const todo = homeworks
      .filter((hw) => !hw.submission)
      .sort((a, b) => dueTs(a) - dueTs(b));
    const done = homeworks
      .filter((hw) => hw.submission)
      .sort((a, b) => dueTs(b) - dueTs(a));
    return { todo, list: [...todo, ...done].slice(0, 5) };
  }, [homeworks]);

  const renderJoin = (lesson) => {
    if (!lesson.meeting_url) {
      return <span className="student-home__note">{tr("lessons.noLink")}</span>;
    }
    if (isWithinMeetingJoinWindow(lesson)) {
      return (
        <a
          href={lesson.meeting_url}
          className="btn primary"
          target="_blank"
          rel="noreferrer"
        >
          <i className="fa-solid fa-video" aria-hidden="true" />
          {tr("lessons.join")}
        </a>
      );
    }
    return (
      <span className="student-home__note">
        {tr("lessons.joinSoon", {
          time:
            MEETING_JOIN_WINDOW_MINUTES % 60 === 0
              ? `${MEETING_JOIN_WINDOW_MINUTES / 60} h`
              : `${MEETING_JOIN_WINDOW_MINUTES} min`,
        })}
      </span>
    );
  };

  const teacherName = (lesson) =>
    lesson.teacher?.full_name || lesson.teacher?.email || "";

  const homeworkStatus = (hw) => {
    const submission = hw.submission;
    if (submission?.status === "reviewed") {
      const score = Number(submission.score);
      const max = Number(submission.max_score);
      if (Number.isFinite(score) && Number.isFinite(max) && max > 0) {
        const round = (n) => Math.round(n * 10) / 10;
        return {
          tone: "done",
          text: tr("homework.score", { score: round(score), max: round(max) }),
        };
      }
      return { tone: "done", text: tr("homework.reviewed") };
    }
    if (submission) return { tone: "sent", text: tr("homework.submitted") };
    const due = hw.due_at ? new Date(hw.due_at) : null;
    if (due && due.getTime() < Date.now()) {
      return { tone: "late", text: tr("homework.late") };
    }
    return null;
  };

  return (
    <section className="student-home" aria-label={student.full_name}>
      <header className="student-home__header">
        <h1>{tr("greeting", { name: firstName })}</h1>
        <p>{tr("subtitle")}</p>
        {classNames.length ? (
          <ul className="student-home__classes">
            {classNames.map((name) => (
              <li key={name}>
                <i className="fa-solid fa-users" aria-hidden="true" />
                {name}
              </li>
            ))}
          </ul>
        ) : null}
      </header>

      <div className="student-home__grid">
        <section className="student-home__card">
          <header className="student-home__cardHead">
            <h2>
              <i className="fa-regular fa-calendar" aria-hidden="true" />
              {tr("lessons.title")}
            </h2>
            <Link to="/lessons" className="student-home__link">
              {tr("lessons.viewAll")}
            </Link>
          </header>

          {!nextLesson ? (
            <p className="student-home__empty">{tr("lessons.empty")}</p>
          ) : (
            <>
              <article className="student-home__next">
                <span className="student-home__eyebrow">
                  {new Date(nextLesson.scheduled_at).getTime() <= Date.now()
                    ? tr("lessons.now")
                    : tr("lessons.next")}
                </span>
                <h3>{nextLesson.title}</h3>
                <p className="student-home__when">
                  {dayLabel(new Date(nextLesson.scheduled_at))} ·{" "}
                  {timeFormatter.format(new Date(nextLesson.scheduled_at))}
                  {nextLesson.duration_minutes
                    ? ` · ${tr("lessons.minutes", {
                        count: nextLesson.duration_minutes,
                      })}`
                    : ""}
                </p>
                {teacherName(nextLesson) ? (
                  <p className="student-home__meta">
                    {tr("lessons.with", { name: teacherName(nextLesson) })}
                  </p>
                ) : null}
                <div className="student-home__action">{renderJoin(nextLesson)}</div>
              </article>

              {laterLessons.length ? (
                <ul className="student-home__list">
                  {laterLessons.map((lesson) => {
                    const when = new Date(lesson.scheduled_at);
                    return (
                      <li key={`${lesson.id}-${lesson.scheduled_at}`}>
                        <span className="student-home__date">
                          <strong>{when.getDate()}</strong>
                          <small>
                            {shortDateFormatter
                              .formatToParts(when)
                              .find((p) => p.type === "month")?.value || ""}
                          </small>
                        </span>
                        <span className="student-home__itemText">
                          <strong>{lesson.title}</strong>
                          <small>
                            {dayLabel(when)} · {timeFormatter.format(when)}
                            {teacherName(lesson)
                              ? ` · ${teacherName(lesson)}`
                              : ""}
                          </small>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </>
          )}
        </section>

        <section className="student-home__card">
          <header className="student-home__cardHead">
            <h2>
              <i className="fa-solid fa-book" aria-hidden="true" />
              {tr("homework.title")}
              {homeworkItems.todo.length ? (
                <span className="student-home__count">
                  {tr("homework.toDo", { count: homeworkItems.todo.length })}
                </span>
              ) : null}
            </h2>
            <Link to="/homeworks" className="student-home__link">
              {tr("homework.viewAll")}
            </Link>
          </header>

          {homeworksLoading && !homeworks.length ? (
            <div className="student-home__spinner" aria-hidden="true" />
          ) : !homeworkItems.list.length ? (
            <p className="student-home__empty">{tr("homework.empty")}</p>
          ) : (
            <ul className="student-home__list">
              {homeworkItems.list.map((hw) => {
                const status = homeworkStatus(hw);
                const due = hw.due_at ? new Date(hw.due_at) : null;
                return (
                  <li key={hw.id}>
                    <span
                      className={`student-home__dot${
                        hw.submission ? " is-done" : ""
                      }`}
                      aria-hidden="true"
                    >
                      <i
                        className={
                          hw.submission ? "fa-solid fa-check" : "fa-solid fa-pen"
                        }
                      />
                    </span>
                    <span className="student-home__itemText">
                      <strong>{hw.title || `#${hw.id}`}</strong>
                      <small>
                        {due
                          ? tr("homework.due", {
                              date: `${dayLabel(due)} ${timeFormatter.format(due)}`,
                            })
                          : tr("homework.noDue")}
                      </small>
                      {status ? (
                        <span className={`student-home__status is-${status.tone}`}>
                          {status.text}
                        </span>
                      ) : null}
                    </span>
                    <Link
                      to={`/homeworks/${hw.id}`}
                      className={`btn ${hw.submission ? "ghost" : "primary"}`}
                    >
                      {hw.submission ? tr("homework.view") : tr("homework.start")}
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </section>
  );
}

//
